import React, { useCallback, useEffect, useState } from "react";
import { BookOutlined, PlusOutlined } from "@ant-design/icons";
import CcButton from "../CoilConfigurator/components/CcButton";
import ConfigurationsDashboard from "./views/ConfigurationsDashboard";
import NewConfigurationWizard, { type WizardStep } from "./views/NewConfigurationWizard";
import GeneratingView from "./views/GeneratingView";
import ConfigurationResultView from "./views/ConfigurationResultView";
import EquipmentDetailView from "./views/EquipmentDetailView";
import CataloguePanel from "./views/CataloguePanel";
import AssumptionsPanel from "./views/AssumptionsPanel";
import GlossaryModal from "./components/GlossaryModal";
import EditConfigurationModal from "./components/EditConfigurationModal";
import OpTabNav, { type OpTab } from "./components/OpTabNav";
import ConfirmDialog from "../CoilConfigurator/components/ConfirmDialog";
import { OnPipeApiError } from "../../services/onPipeConfiguratorApi";
import { configurationService } from "./services/configurationService";
import { wizardStepForField } from "./utils/validationFieldMap";
import {
  emptyPipeSpecification,
  emptyProcessSpecification,
  emptyProjectInformation,
  type CadCheck,
  type ConfigurationResult,
  type EquipmentKind,
  type PipeSpecification,
  type ProcessSpecification,
  type ProjectInformation,
  type ProjectSpecification,
} from "./models/types";
import "../CoilConfigurator/coil-configurator.css";
import "../CoilConfigurator/coil-configurator-legacy.css";
import "../CoilConfigurator/components/cc-button.css";
import "../CoilConfigurator/components/cc-dialog.css";
import "../CoilConfigurator/components/input-with-unit-select.css";
import "./on-pipe-configurator.css";

type Screen = "list" | "wizard" | "generating" | "result" | "equipment-detail";

const WIZARD_ORDER: WizardStep[] = ["project", "pipe", "process", "review"];

const emptySpec = (): ProjectSpecification => ({
  project: emptyProjectInformation(),
  pipe: emptyPipeSpecification(),
  process: emptyProcessSpecification(),
});

const GENERATE_STEPS = [
  "Analysing pipe specification",
  "Calculating Blast Frame",
  "Calculating Induction Coil",
  "Generating configuration",
];

const OnPipeConfiguratorPage = () => {
  const [activeTab, setActiveTab] = useState<OpTab>("configurations");
  const [screen, setScreen] = useState<Screen>("list");
  const [rows, setRows] = useState<ConfigurationResult[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [spec, setSpec] = useState<ProjectSpecification>(emptySpec);
  const [step, setStep] = useState<WizardStep>("project");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ConfigurationResult | null>(null);
  const [equipmentKind, setEquipmentKind] = useState<EquipmentKind>("blast-frame");
  const [generateProgress, setGenerateProgress] = useState(0);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ConfigurationResult | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConfigurationResult | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await configurationService.listConfigurations();
      setRows(data);
    } catch (error) {
      setRows([]);
      setListError(
        error instanceof Error
          ? error.message
          : "We couldn't load configurations. Check that the On-Pipe API is reachable.",
      );
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const startCreate = () => {
    setActiveTab("configurations");
    setSpec(emptySpec());
    setStep("project");
    setErrors({});
    setGenerateError(null);
    setScreen("wizard");
  };

  const validateStep = (current: WizardStep) => {
    const nextErrors: Record<string, string> = {};
    if (current === "project") {
      if (!spec.project.projectNumber.trim()) nextErrors.projectNumber = "Project number is required.";
      if (!spec.project.projectName.trim()) nextErrors.projectName = "Project name is required.";
      if (!spec.project.customer.trim()) nextErrors.customer = "Customer is required.";
      if (!spec.project.engineer.trim()) nextErrors.engineer = "Engineer is required.";
    }
    if (current === "pipe") {
      if (!spec.pipe.outsideDiameter.trim() || Number(spec.pipe.outsideDiameter) <= 0) {
        nextErrors.outsideDiameter = "Enter a valid pipe outside diameter.";
      }
      if (!spec.pipe.wallThickness.trim() || Number(spec.pipe.wallThickness) <= 0) {
        nextErrors.wallThickness = "Enter a valid wall thickness.";
      }
      if (!spec.pipe.pipeGrade.trim()) nextErrors.pipeGrade = "Pipe grade is required.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const continueWizard = () => {
    if (!validateStep(step)) return;
    const index = WIZARD_ORDER.indexOf(step);
    setStep(WIZARD_ORDER[Math.min(index + 1, WIZARD_ORDER.length - 1)]);
  };

  const backWizard = () => {
    const index = WIZARD_ORDER.indexOf(step);
    setStep(WIZARD_ORDER[Math.max(index - 1, 0)]);
  };

  const generate = async () => {
    setGenerateError(null);
    setGenerateProgress(0);
    setScreen("generating");
    const timers = [0, 1, 2, 3].map((i) =>
      window.setTimeout(() => setGenerateProgress(i + 1), 280 * (i + 1)),
    );
    try {
      const created = await configurationService.createConfiguration(spec);
      timers.forEach((id) => window.clearTimeout(id));
      setGenerateProgress(4);
      setResult(created);
      setActiveTab("configurations");
      setScreen("result");
      await loadList();
    } catch (error) {
      timers.forEach((id) => window.clearTimeout(id));
      if (error instanceof OnPipeApiError && error.validationErrors?.length) {
        const fieldErrors = Object.fromEntries(
          error.validationErrors.map((item) => [item.field, item.message]),
        );
        setErrors(fieldErrors);
        setGenerateError(error.message);
        setStep(wizardStepForField(error.validationErrors[0].field));
      } else {
        setGenerateError(
          error instanceof Error
            ? error.message
            : "We couldn't generate the configuration. Please try again or contact engineering support.",
        );
        setStep("review");
      }
      setScreen("wizard");
    }
  };

  const openResult = async (id: string) => {
    setListError(null);
    try {
      const found = await configurationService.getConfiguration(id);
      setResult(found);
      setActiveTab("configurations");
      setScreen("result");
    } catch (error) {
      setListError(
        error instanceof Error ? error.message : "We couldn't open this configuration.",
      );
    }
  };

  const openEdit = async (id: string) => {
    setEditError(null);
    try {
      const found = await configurationService.getConfiguration(id);
      setEditTarget(found);
    } catch (error) {
      setListError(
        error instanceof Error ? error.message : "We couldn't open this configuration for edit.",
      );
    }
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditTarget(null);
    setEditError(null);
  };

  const saveAndRecalculate = async (nextSpec: ProjectSpecification) => {
    if (!editTarget) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await configurationService.updateConfiguration(
        editTarget.configurationId,
        nextSpec,
      );
      setEditTarget(null);
      setResult(updated);
      setActiveTab("configurations");
      setScreen("result");
      await loadList();
    } catch (error) {
      setEditError(
        error instanceof Error
          ? error.message
          : "We couldn't save and recalculate this configuration.",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const requestDelete = async (id: string) => {
    setDeleteError(null);
    if (result?.configurationId === id) {
      setDeleteTarget(result);
      return;
    }
    const found = rows.find((row) => row.configurationId === id);
    if (found) {
      setDeleteTarget(found);
      return;
    }
    try {
      const loaded = await configurationService.getConfiguration(id);
      setDeleteTarget(loaded);
    } catch (error) {
      setListError(
        error instanceof Error ? error.message : "We couldn't load this configuration to delete.",
      );
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await configurationService.deleteConfiguration(deleteTarget.configurationId);
      setDeleteTarget(null);
      if (result?.configurationId === deleteTarget.configurationId) {
        setResult(null);
        setScreen("list");
      }
      if (editTarget?.configurationId === deleteTarget.configurationId) {
        setEditTarget(null);
      }
      await loadList();
    } catch (error) {
      setDeleteError(
        error instanceof Error ? error.message : "We couldn't delete this configuration.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const toggleCad = async (checks: CadCheck[]) => {
    if (!result) return;
    try {
      const updated = await configurationService.updateCadChecks(
        result.configurationId,
        equipmentKind,
        checks,
      );
      setResult({ ...updated });
    } catch (error) {
      setListError(
        error instanceof Error ? error.message : "We couldn't update CAD checks.",
      );
    }
  };

  return (
    <div className="app-shell coil-configurator-shell on-pipe-configurator-shell">
      <div className="coil-configurator-main">
        <header className="page-header">
          <div className="page-header__identity">
            <h1 className="page-header__title">On-Pipe Equipment Configurator</h1>
            <p className="page-header__breadcrumb">Engineering · Blast Frame + Induction Coil</p>
          </div>
          <div className="page-header__actions">
            <CcButton
              variant="secondary"
              className="glossary-button"
              icon={<BookOutlined />}
              onClick={() => setGlossaryOpen(true)}
            >
              Glossary
            </CcButton>
            <CcButton variant="primary" icon={<PlusOutlined />} onClick={startCreate}>
              New Configuration
            </CcButton>
          </div>
        </header>

        <div className="content-container">
          <OpTabNav activeTab={activeTab} onChange={setActiveTab} />

          {generateError && activeTab === "configurations" ? (
            <p className="op-field-error" role="alert">
              {generateError}
            </p>
          ) : null}

          {listError && activeTab === "configurations" ? (
            <p className="op-field-error" role="alert">
              {listError}
            </p>
          ) : null}

          {deleteError && activeTab === "configurations" && !deleteTarget ? (
            <p className="op-field-error" role="alert">
              {deleteError}
            </p>
          ) : null}

          {activeTab === "configurations" && screen === "list" && (
            <ConfigurationsDashboard
              rows={rows}
              loading={listLoading}
              onOpen={openResult}
              onEdit={openEdit}
              onDelete={requestDelete}
            />
          )}

          {activeTab === "configurations" && screen === "wizard" && (
            <NewConfigurationWizard
              step={step}
              spec={spec}
              errors={errors}
              onChangeProject={(patch: Partial<ProjectInformation>) =>
                setSpec((prev) => ({ ...prev, project: { ...prev.project, ...patch } }))
              }
              onChangePipe={(patch: Partial<PipeSpecification>) =>
                setSpec((prev) => ({ ...prev, pipe: { ...prev.pipe, ...patch } }))
              }
              onChangeProcess={(patch: Partial<ProcessSpecification>) =>
                setSpec((prev) => ({ ...prev, process: { ...prev.process, ...patch } }))
              }
              onBack={backWizard}
              onContinue={continueWizard}
              onCancel={() => setScreen("list")}
              onGenerate={generate}
            />
          )}

          {activeTab === "configurations" && screen === "generating" && (
            <GeneratingView
              steps={GENERATE_STEPS.map((label, index) => ({
                label,
                state: index < generateProgress ? "done" : index === generateProgress ? "active" : "pending",
              }))}
            />
          )}

          {activeTab === "configurations" && screen === "result" && result && (
            <ConfigurationResultView
              result={result}
              onBackToList={() => setScreen("list")}
              onViewEquipment={(kind) => {
                setEquipmentKind(kind);
                setScreen("equipment-detail");
              }}
            />
          )}

          {activeTab === "configurations" && screen === "equipment-detail" && result && (
            <EquipmentDetailView
              result={result}
              equipmentKind={equipmentKind}
              onBack={() => setScreen("result")}
              onToggleCad={toggleCad}
            />
          )}

          {activeTab === "catalogue" && <CataloguePanel />}

          {activeTab === "assumptions" && <AssumptionsPanel />}
        </div>
      </div>

      <GlossaryModal open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
      <EditConfigurationModal
        open={Boolean(editTarget)}
        result={editTarget}
        saving={editSaving}
        error={editError}
        onClose={closeEdit}
        onSave={saveAndRecalculate}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Configuration"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.project.projectNumber || deleteTarget.configurationId}"? This cannot be undone.${
                deleteError ? ` ${deleteError}` : ""
              }`
            : ""
        }
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        loading={deleting}
        onClose={closeDelete}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default OnPipeConfiguratorPage;
